import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getUsers } from 'common/services/users.service';
import debounce from 'debounce-promise';

const getUsersDebounced = debounce(getUsers, 500);

const useUsersQuery = ({
  limit = 10,
  orderBy = 'name',
  orderDir = 'ASC',
  search = '',
  searchFields = ['name', 'email']
} = {}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: ['users', limit, orderBy, orderDir, search, searchFields],
    queryFn: ({ pageParam = 0 }) =>
      getUsersDebounced({
        limit,
        offset: pageParam,
        orderBy,
        orderDir,
        search,
        searchFields
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * limit;
      return currentOffset < lastPage.responseObject.total
        ? currentOffset
        : undefined;
    },
    initialPageParam: 0
  });

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  };

  const users = useMemo(
    () => data?.pages?.flatMap(page => page.responseObject.users) || [],
    [data?.pages]
  );
  const total = data?.pages?.[0]?.responseObject?.total || 0;

  return {
    data: users,
    total,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error,
    loadMore,
    hasNextPage
  };
};

export default useUsersQuery;
